import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function isVideoUrl(u) {
  return /\.(mp4|webm|mov|ogg)(\?|#|$)/i.test(u || "");
}

export default function ImageUpload({ value, onChange, testId, compact = false, allowVideo = false }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — try a smaller image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Upload a photo or paste a link"
          className="flex-1"
        />
        <button
          type="button"
          data-testid={`${testId}-upload-btn`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E4DBD0] dark:border-[#352F2B] px-3.5 text-sm font-medium text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={allowVideo ? "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png,image/webp,image/gif"}
          className="hidden"
          onChange={pick}
          data-testid={`${testId}-file-input`}
        />
      </div>
      {!compact && value && (
        isVideoUrl(value) ? (
          <video src={value} controls playsInline className="h-32 w-full rounded-lg object-cover bg-black border border-[#E4DBD0] dark:border-[#352F2B]" />
        ) : (
          <img
            src={value}
            alt="Preview"
            className="h-32 w-full rounded-lg object-cover border border-[#E4DBD0] dark:border-[#352F2B]"
          />
        )
      )}
    </div>
  );
}
